import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import organizationsApi from '../utils/organizationsApi';

/**
 * Minimal shell-level active-organization selector.
 *
 * On mount (once, unmount-guarded — no callback-prop dependency) it loads the
 * user's memberships and keeps only ACTIVE ones. It renders NOTHING for users
 * with fewer than two active memberships (single-org users see no change).
 * For genuine multi-org users it renders a compact dropdown; selecting a
 * different org calls switchOrganization then reloads so every org-scoped view
 * re-derives against the new active org (per the locked no-JWT-change decision).
 *
 * @param {{ activeOrgId?: string }} props - the current active org id (from the
 *   logged-in user's profile), used only to preselect the dropdown.
 */
function OrgSelector({ activeOrgId }) {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [switching, setSwitching] = useState(false);
  // Hold the latest activeOrgId in a ref so the mount-once fetch effect never
  // needs it as a dependency (CLAUDE.md effect-safety).
  const activeOrgIdRef = useRef(activeOrgId);
  activeOrgIdRef.current = activeOrgId;

  useEffect(() => {
    let cancelled = false;
    organizationsApi
      .listMemberships()
      .then((data) => {
        if (cancelled) return;
        const active = (Array.isArray(data) ? data : []).filter((m) => m.status === 'active');
        setMemberships(active);
      })
      .catch(() => {
        if (!cancelled) setMemberships([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleChange(ev) {
    const nextId = ev.target.value;
    if (!nextId || nextId === activeOrgIdRef.current) return;
    setSwitching(true);
    setError('');
    try {
      await organizationsApi.switchOrganization(nextId);
      // Full reload so all org-scoped data re-derives server-side.
      window.location.reload();
    } catch (err) {
      setError('Could not switch organization.');
      setSwitching(false);
    }
  }

  // Only genuine multi-org users get a selector.
  if (loading || memberships.length < 2) return null;

  const selected = memberships.find((m) => m.organization_id === activeOrgId)?.organization_id || memberships[0].organization_id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          border: '1px solid var(--ab-border-hair, #D5DAE1)',
          background: 'var(--ab-bg, #fff)',
          padding: '0 10px',
          opacity: switching ? 0.5 : 1,
        }}
      >
        <select
          aria-label="Active organization"
          value={selected}
          onChange={handleChange}
          disabled={switching}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--ab-fg, #000)',
            minWidth: 0,
            padding: '9px 0',
            appearance: 'none',
            cursor: switching ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--ab-font-sans, inherit)',
          }}
        >
          {memberships.map((m) => (
            <option key={m.organization_id} value={m.organization_id}>
              {m.name}
            </option>
          ))}
        </select>
        <span style={{ color: 'var(--ab-fg-subtle, #64748B)', display: 'inline-flex', pointerEvents: 'none' }}>
          <ChevronDown size={15} />
        </span>
      </div>
      {error && <span style={{ fontSize: 11, color: '#FF0004' }}>{error}</span>}
    </div>
  );
}

export default OrgSelector;
